/**
 * Dashboard canvas (de)serialization
 *
 * A dashboard is a SINGLE FLAT canvas (no pages, no PDF, no comparison) bound to
 * one client — the persisted blob is the versioned `DashboardCanvas`:
 *
 *   { schemaVersion: 1, grid: { columns: 12 }, widgets: WidgetConfig[] }
 *
 * The widget shape is the SAME `ReportPageWidget`/`WidgetConfig` the report
 * editor + renderer use, so the report canvas components consume a dashboard's
 * widgets byte-for-byte. These two helpers are the only place the editor host
 * touches the wire format:
 *
 *   - `toCanvasLayout(widgets)`   editor widgets → wire blob (for POST/PUT)
 *   - `fromCanvasLayout(payload)` wire blob → editor widgets (tolerant of a
 *                                 missing / old / corrupt shape → [])
 *
 * Mirrors the server `canvasLayoutSchema` (apps/server/src/routes/dashboards/
 * crud.ts) so a round-trip validates on the way back in.
 */

import type { WidgetConfig } from "./templateUtils";

/** The dashboard's 12-column grid (fixed; matches the server schema). */
export const DASHBOARD_GRID = { columns: 12 } as const;

/** The current canvas schema version (matches the server `z.literal(1)`). */
export const DASHBOARD_SCHEMA_VERSION = 1 as const;

/** The persisted dashboard canvas blob. */
export interface DashboardCanvasLayout {
  schemaVersion: 1;
  grid: { columns: number };
  widgets: WidgetConfig[];
}

/**
 * Serialize the editor's widgets into the versioned canvas blob the API stores.
 * Only the persisted widget fields are carried through (id/type/title/position/
 * config + optional autoSize) so transient editor-only fields never leak to the
 * server (the report host's save block does the same projection).
 */
export function toCanvasLayout(
  widgets: readonly WidgetConfig[] | null | undefined,
): DashboardCanvasLayout {
  const safe = Array.isArray(widgets) ? widgets : [];
  return {
    schemaVersion: DASHBOARD_SCHEMA_VERSION,
    grid: { columns: DASHBOARD_GRID.columns },
    widgets: safe.map((w) => ({
      id: w.id,
      type: w.type,
      title: w.title,
      position: {
        row: w.position.row,
        col: w.position.col,
        colSpan: w.position.colSpan,
        ...(w.position.rowSpan != null ? { rowSpan: w.position.rowSpan } : {}),
      },
      config: w.config ?? {},
      ...(w.autoSize != null ? { autoSize: w.autoSize } : {}),
    })),
  };
}

/** Type guard: a value looks like a usable widget entry. */
function isWidgetLike(v: unknown): v is WidgetConfig {
  if (!v || typeof v !== "object") return false;
  const w = v as Record<string, unknown>;
  if (typeof w.id !== "string" || typeof w.type !== "string") return false;
  const pos = w.position as Record<string, unknown> | undefined;
  if (!pos || typeof pos !== "object") return false;
  return (
    typeof pos.row === "number" &&
    typeof pos.col === "number" &&
    typeof pos.colSpan === "number"
  );
}

/**
 * Deserialize a stored canvas blob back into editor widgets. Tolerant of every
 * degraded shape — `null`/`undefined`, a non-object, a missing/non-array
 * `widgets`, or individual garbage widget entries — yielding `[]` (or the valid
 * subset) instead of throwing. This is the load-side safety net that keeps a
 * corrupt row from crashing the editor (mirrors the report host's defensive
 * parse) and lets autosave re-persist a clean canvas.
 */
export function fromCanvasLayout(payload: unknown): WidgetConfig[] {
  if (!payload || typeof payload !== "object") return [];
  const obj = payload as Record<string, unknown>;
  const widgets = obj.widgets;
  if (!Array.isArray(widgets)) return [];
  return widgets.filter(isWidgetLike).map((w) => ({
    id: w.id,
    type: w.type,
    title: typeof w.title === "string" ? w.title : "",
    position: {
      row: w.position.row,
      col: w.position.col,
      colSpan: w.position.colSpan,
      ...(w.position.rowSpan != null ? { rowSpan: w.position.rowSpan } : {}),
    },
    config:
      w.config && typeof w.config === "object"
        ? (w.config as Record<string, unknown>)
        : {},
    ...(typeof w.autoSize === "boolean" ? { autoSize: w.autoSize } : {}),
  }));
}
